import {
  useMutation as useQueryMutation,
  type DefaultError,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useRef } from 'react';

/** Ignores `mutate` while a call is in flight; `isPending` re-renders a tick too late to stop a double click. */
export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const inFlight = useRef(false);
  const mutation = useQueryMutation({
    ...options,
    onSettled: (...args) => {
      inFlight.current = false;
      return options.onSettled?.(...args);
    },
  });
  return {
    ...mutation,
    mutate: (variables, callbacks) => {
      if (inFlight.current) return;
      inFlight.current = true;
      mutation.mutate(variables, callbacks);
    },
  };
}
